import { FC } from 'react';
import { Alert, Box, Link, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import EmailIcon from '@mui/icons-material/Email';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import NoRiderAssistantPhoto from '../../../../assets/images/empty_ra.png';
import { Card } from '../../../../shared/components/Card';
import { CopyToClipboard } from '../../../../shared/components/CopyToClipboard';
import { PhotoOrInitials } from '../../../../shared/components/PhotoOrInitials';
import { colorPalette, colorTokens } from '../../../../shared/components/variables';
import { ErrorBoundary } from '../../../../shared/utils/errorReporting/ErrorBoundary';
import { CarePartnerCard } from '../../../components/CarePartnerCard';
import { SkeletonRides, UpcomingRides } from '../../../components/UpcomingRides';
import { IRiderAssistantDetails } from '../../../types/riderAssistant';
import { capitalizePronouns } from '../../../utils/helpers/name';

import { NoRiderAssistantContainer, StyledAlert } from './RiderAssistantPresenter.styles';

interface RiderAssistantPresenterProps {
  isFetching?: boolean;
  adminRideUrl?: string;
  riderDetails?: IRiderAssistantDetails;
  riderAssistantId?: string;
  isRemovedRider?: boolean;
}

export const RiderAssistantPresenter: FC<RiderAssistantPresenterProps> = ({
  riderDetails,
  adminRideUrl,
  riderAssistantId,
  isFetching = false,
  isRemovedRider = false,
}) => {
  const carePartnerDetails = riderDetails?.carePartnerDetails;
  const noRiderAssistant = !isFetching && !riderDetails?.slug;

  if (isFetching) {
    return <SkeletonRiderAssistantDetails />;
  }

  return noRiderAssistant ? (
    <NoRiderAssistant carePartnerDetails={carePartnerDetails} adminRideUrl={adminRideUrl} />
  ) : (
    <Card>
      <Card.Header>
        <Text variant="h6" style={{ color: colorTokens.primary.main }}>
          Rider Assistant
        </Text>
        <CopyToClipboard
          toastMessage="Slug Copied"
          text={riderDetails?.slug || ''}
          variant="h6"
          color={colorTokens.text.secondary}
        />
      </Card.Header>
      {isRemovedRider && (
        <Alert
          variant="filled"
          severity="success"
          message="This Rider Assistant has been removed from the ride"
          style={{ backgroundColor: colorPalette.neutral350, fontSize: '20px' }}
        />
      )}
      <Card.Body>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
          <PhotoOrInitials
            fullName={`${riderDetails?.firstName} ${riderDetails?.lastName}`}
            pictureUrl={riderDetails?.profilePhotoUrl}
          />
          <Link
            target="_blank"
            variant="h5"
            underline="hover"
            href={riderDetails?.adminUrl}
            style={{ color: colorTokens.text.primary, fontWeight: 500 }}
          >
            {`${riderDetails?.firstName} ${riderDetails?.lastName}`}
          </Link>
          <Text variant="body1" fontWeight={300}>
            {capitalizePronouns(riderDetails?.pronouns || '')}
          </Text>
        </Box>
        <Box mb={3} display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start" gap="12px">
          <InnerContainer>
            <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
            <CopyToClipboard
              toastMessage="Phone Number Copied"
              text={riderDetails?.mobilePhoneNumber || ''}
              variant="h6"
              color={colorTokens.text.secondary}
              textStyle={{ marginRight: '9px' }}
            />
          </InnerContainer>
          <InnerContainer>
            <EmailIcon style={{ color: colorTokens.text.secondary }} />
            <CopyToClipboard
              toastMessage="Email Copied"
              text={riderDetails?.email || ''}
              variant="h6"
              color={colorTokens.text.secondary}
              truncate={true}
              textStyle={{ marginRight: '9px' }}
            />
          </InnerContainer>
          <InnerContainer>
            <Svg icon="location-dot" width="24" height="24" style={{ color: colorTokens.text.secondary }} />
            <CopyToClipboard
              toastMessage="Address Copied"
              text={riderDetails?.address || ''}
              variant="h6"
              color={colorTokens.text.secondary}
              textStyle={{ marginRight: '9px' }}
            />
          </InnerContainer>
          <InnerContainer gap="5px">
            <Svg icon="directions-car-filled" width="16" height="16" style={{ color: colorTokens.text.secondary }} />
            <Text variant="caption" textColor="secondaryText" fontWeight={500} lineHeight="18px">
              {riderDetails?.rideCount} Trips Activated {riderDetails?.activatedOn}
            </Text>
          </InnerContainer>
        </Box>
        <CarePartnerCard
          name={carePartnerDetails?.name || ''}
          certifiedDriversCount={carePartnerDetails?.certifiedDriverCount}
          phoneNumber={carePartnerDetails?.businessPhoneNumber}
          url={carePartnerDetails?.adminCarePartnerDetailsLink}
        />
        <Box gap="4px" width="100%">
          <ErrorBoundary FallbackComponent={SkeletonRides}>
            <UpcomingRides riderAssistantId={riderAssistantId?.toString() || ''} />
          </ErrorBoundary>
        </Box>
      </Card.Body>
    </Card>
  );
};

const InnerContainer = ({ children, ...rest }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px" {...rest}>
      {children}
    </Box>
  );
};

export const SkeletonRiderAssistantDetails = () => {
  return (
    <Card>
      <Card.Header>
        <Text variant="h6" style={{ color: colorTokens.primary.main }}>
          Rider Assistant
        </Text>
        <Skeleton variant="text" width={135} height={32} />
      </Card.Header>
      <Card.Body>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
          <Skeleton data-testid="header-rider-assistant-skeleton" variant="circular" width={92} height={92} />
          <Skeleton variant="text" width={224} height={42} />
          <Skeleton variant="text" width={58} height={24} />
        </Box>
        <Box mb={3} display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start" gap="12px">
          <InnerContainer>
            <Skeleton data-testid="rider-assistant-skeleton-loader" variant="text" width={24} height={32} />
            <Skeleton variant="text" width={129} height={32} />
            <Skeleton variant="text" width={24} height={32} />
          </InnerContainer>
          <InnerContainer>
            <Skeleton data-testid="rider-assistant-skeleton-loader" variant="text" width={24} height={32} />
            <Skeleton variant="text" width={129} height={32} />
            <Skeleton variant="text" width={24} height={32} />
          </InnerContainer>
          <InnerContainer gap="5px">
            <Skeleton data-testid="rider-assistant-skeleton-loader" variant="text" width={24} height={32} />
            <Skeleton variant="text" width={129} height={32} />
            <Skeleton variant="text" width={24} height={32} />
          </InnerContainer>
          <InnerContainer gap="5px">
            <Skeleton data-testid="rider-assistant-skeleton-loader" variant="text" width={18} height={18} />
            <Skeleton variant="text" width={155} height={18} />
          </InnerContainer>
        </Box>
        <CarePartnerCard isFetching={true} />
      </Card.Body>
    </Card>
  );
};

const NoRiderAssistant = ({ adminRideUrl, carePartnerDetails }) => {
  return (
    <Card>
      <Card.Header>
        <Text variant="h6" style={{ color: colorTokens.primary.main }}>
          Rider Assistant
        </Text>
      </Card.Header>
      <Card.Body>
        <StyledAlert
          severity="warning"
          variant="filled"
          title="Rider Assistant can be added in Admin"
          message=""
          action={
            <Link
              data-testid="admin-link"
              target="_blank"
              href={adminRideUrl}
              style={{
                textDecoration: 'none',
              }}
            >
              <Text variant="button">OPEN IN ADMIN</Text>
            </Link>
          }
        />
        <CarePartnerCard
          name={carePartnerDetails?.name || ''}
          certifiedDriversCount={carePartnerDetails?.certifiedDriverCount}
          phoneNumber={carePartnerDetails?.businessPhoneNumber}
          url={carePartnerDetails?.adminCarePartnerDetailsLink}
        />
        <NoRiderAssistantContainer>
          <Box p={20}>
            <img src={NoRiderAssistantPhoto} alt="No Vehicle" />
          </Box>
        </NoRiderAssistantContainer>
      </Card.Body>
    </Card>
  );
};
